import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, MousePointerClick, Users, ShieldCheck, ShoppingBag, Store, Smartphone, Apple, CreditCard } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, apiError } from "../lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";

const LABELS = {
    android_store_click: { label: "Clics Google Play", icon: Smartphone },
    ios_store_click: { label: "Clics App Store", icon: Apple },
    boutique_click: { label: "Accès boutique", icon: ShoppingBag },
    merchant_outbound_click: { label: "Clics marchands", icon: Store },
    website_account_created: { label: "Comptes créés (site)", icon: Users },
    website_account_verified: { label: "Comptes vérifiés", icon: ShieldCheck },
    android_install_verified: { label: "Installations Android vérifiées", icon: Smartphone },
    ios_install_verified: { label: "Installations iOS vérifiées", icon: Apple },
    purchase_confirmed: { label: "Achats confirmés", icon: CreditCard },
};

export default function Audience() {
    const [days, setDays] = useState(30);
    const [d, setD] = useState(null);
    const [fed, setFed] = useState(null);

    const load = () => {
        api.get("/admin/audience", { params: { days } }).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
        api.get("/admin/federation").then((r) => setFed(r.data)).catch(() => setFed(null));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

    const toggleCounter = async (visible) => {
        try {
            await api.put("/admin/audience/counter", { visible });
            toast.success(visible ? "Compteur public affiché." : "Compteur public masqué.");
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const toggleDedupLegal = async (validated) => {
        try {
            await api.put("/admin/federation/dedup-legal", { validated });
            toast.success(validated ? "Déduplication inter-projets activée (validation juridique confirmée)." : "Déduplication inter-projets désactivée.");
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const exportCsv = async () => {        try {
            const r = await api.get("/admin/audience/export", { params: { days }, responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `audience_${days}j.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Export CSV téléchargé.");
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    if (!d) return <p className="text-ink-soft">Chargement…</p>;

    const chartData = (d.series || []).map((s) => ({ date: s.date, total: Object.entries(s).filter(([k]) => k !== "date").reduce((a, [, v]) => a + v, 0) }));

    return (
        <div data-testid="admin-audience">
            <PageHeader title="Audience & Conversions" subtitle={d.dedup_rule + " Un clic n'est jamais compté comme un téléchargement, une installation ou un achat."}>
                <div className="flex gap-2">
                    {[7, 30, 90].map((n) => (
                        <button key={n} onClick={() => setDays(n)} className={`px-4 py-1.5 rounded-full text-sm border ${days === n ? "bg-forest text-ivory border-forest" : "border-border text-ink-soft"}`} data-testid={`period-${n}`}>
                            {n} jours
                        </button>
                    ))}
                    <Button variant="outline" className="gap-2" onClick={exportCsv} data-testid="export-csv-button"><Download size={15} /> Export CSV</Button>
                </div>
            </PageHeader>

            <div className="rounded-2xl bg-white border border-gold/40 p-6 shadow-sm mb-6" data-testid="counter-card">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <p className="text-xs font-mono uppercase tracking-widest text-ink-soft">Compteur public de preuve sociale</p>
                        <p className="font-heading font-extrabold text-4xl text-forest mt-1" data-testid="counter-value">{d.counter.count}</p>
                        <p className="text-sm text-ink-soft">{d.counter.label} — affiché au-dessus des témoignages</p>
                        <p className="text-[11px] text-ink-soft/70 mt-2 max-w-lg">Règle canonique : comptes réels du site, rôle utilisateur, actifs, hors comptes internes et de test. Indépendant des comptes de l'application mobile.</p>
                    </div>
                    <label className="flex items-center gap-3 text-sm text-ink">
                        <Switch checked={d.counter.visible} onCheckedChange={toggleCounter} data-testid="counter-visibility-switch" />
                        {d.counter.visible ? "Visible sur le site" : "Masqué sur le site"}
                    </label>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(d.metrics).map(([key, m]) => {
                    const meta = LABELS[key] || { label: key, icon: MousePointerClick };
                    const notConnected = m.status === "non_connecte";
                    return (
                        <div key={key} className="rounded-2xl bg-white border border-border p-5 shadow-sm" data-testid={`metric-${key}`}>
                            <div className="flex items-center justify-between">
                                <p className="text-xs font-mono uppercase tracking-widest text-ink-soft">{meta.label}</p>
                                <meta.icon size={16} className={notConnected ? "text-amber-600" : "text-gold-dark"} />
                            </div>
                            {notConnected ? (
                                <>
                                    <p className="font-heading font-extrabold text-2xl text-ink-soft/60 mt-2">—</p>
                                    <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 mt-2 leading-snug">{d.not_connected[key]}</p>
                                </>
                            ) : (
                                <>
                                    <p className="font-heading font-extrabold text-3xl text-ink mt-2">{m.period}</p>
                                    <p className="text-[11px] text-ink-soft mt-1">sur {d.period_days} jours · total : {m.total}</p>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="mt-6 rounded-2xl bg-white border border-border p-6 shadow-sm" data-testid="federation-card">
                <h2 className="font-heading font-bold text-ink mb-1">Statistiques de l'application (Contrat A — M2M)</h2>
                <p className="text-[11px] text-ink-soft mb-4">Contrat 1.0 · règles d'éligibilité 2026-06.1 · cache source 300 s · unité : comptes, jamais des personnes uniques.</p>
                {!fed ? (
                    <p className="text-sm text-ink-soft">Chargement…</p>
                ) : !fed.config.base_url_configured || !fed.config.m2m_configured ? (
                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900" data-testid="federation-not-configured">
                        <p className="font-semibold">Non connecté</p>
                        <p className="mt-1 text-xs leading-relaxed">
                            URL du backend Application : {fed.config.base_url_configured ? "configurée" : "non configurée"} ·
                            Identifiants M2M : {fed.config.m2m_configured ? "configurés" : "non configurés"} ·
                            Clé de déduplication : {fed.config.dedup_key_configured ? "configurée" : "non configurée"}.
                            La connexion s'activera dès que le client partenaire de production sera provisionné.
                        </p>
                    </div>
                ) : fed.app_stats_error ? (
                    <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-800" data-testid="federation-error-card">
                        Statistiques indisponibles : {fed.app_stats_error}
                    </div>
                ) : fed.app_stats ? (
                    <div className="space-y-4" data-testid="federation-live">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="rounded-xl bg-pearl p-4">
                                <p className="text-xs text-ink-soft">Comptes éligibles (application)</p>
                                <p className="font-heading font-extrabold text-3xl text-forest mt-1">{fed.app_stats.eligible_accounts}</p>
                            </div>
                            <div className="rounded-xl bg-pearl p-4">
                                <p className="text-xs text-ink-soft">Comptes éligibles (site)</p>
                                <p className="font-heading font-extrabold text-3xl text-forest mt-1">{d.counter.count}</p>
                            </div>
                            <div className="rounded-xl bg-pearl p-4">
                                <p className="text-xs text-ink-soft">Doublons confirmés (HMAC)</p>
                                <p className="font-heading font-extrabold text-3xl text-forest mt-1">{fed.dedup.matched ?? "—"}</p>
                            </div>
                        </div>
                        <p className="text-xs text-ink-soft">
                            Fraîcheur source : {fed.app_stats.freshness?.status} ({fed.app_stats.freshness?.age_seconds}s) · calculée le {fed.app_stats.computed_at ? new Date(fed.app_stats.computed_at).toLocaleString("fr-FR") : "—"}
                            {fed.app_stats.site_cache?.status === "stale" && " · dernière valeur connue (source momentanément indisponible)"}
                        </p>
                        <label className="flex items-start gap-3 text-sm text-ink">
                            <Switch checked={fed.dedup.legal_validated} onCheckedChange={toggleDedupLegal} data-testid="dedup-legal-switch" />
                            <span>
                                Déduplication inter-projets activée — je confirme que la base légale (intérêt légitime) et l'information des utilisateurs des deux projets sont validées par le responsable de traitement.
                                {!fed.config.dedup_key_configured && <span className="block text-xs text-amber-800 mt-1">Clé de déduplication non configurée : la déduplication restera inactive.</span>}
                            </span>
                        </label>
                    </div>
                ) : null}
            </div>

            <div className="mt-6 rounded-2xl bg-white border border-border p-6 shadow-sm" data-testid="audience-chart">
                <h2 className="font-heading font-bold text-ink mb-4">Événements de conversion — {d.period_days} derniers jours</h2>
                {chartData.length === 0 ? (
                    <p className="text-sm text-ink-soft py-8 text-center">Aucun événement sur la période. Les événements n'apparaissent qu'avec le consentement mesure d'audience des visiteurs.</p>
                ) : (
                    <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E8E3D9" />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={30} />
                            <Tooltip formatter={(v) => [`${v} événement(s)`, "Total"]} labelFormatter={(v) => new Date(v).toLocaleDateString("fr-FR")} />
                            <Area type="monotone" dataKey="total" stroke="#164B3B" fill="#BE9956" fillOpacity={0.3} strokeWidth={2} />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
}
